import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {repeat} from 'lit/directives/repeat.js';
import {when} from 'lit/directives/when.js';
import {classMap} from 'lit/directives/class-map.js';
import store from '../store';
import {getProductData} from '../util/getProductData';
import {featureInSection} from '../util/featureInSection';
import {regionCheck} from '../util/regionCheck';
import {luminaryOptions} from '../sectionConfig';
import {selectCurrentSection, selectLuminary, selectSectionConfig} from '../selectors';
import {selectSolarLumPosition2IsVisible} from '../solar/solarSelectors';

@customElement('feature-container')
export class FeatureContainer extends connect(store)(LitElement) {

	static styles = css`
		:host {
			height: 100%;
		}
		.container {
			width: var(--features-column-width);
			overflow-y: scroll;
			padding: 0 var(--large-padding) 0 var(--large-padding);
			pointer-events: none;
			height: 100%;
			overflow-x: hidden;
		}
		.container.columns {
			columns: var(--features-column-width) 6;
			column-fill: balance;
			column-gap: var(--features-column-gap);
			width: 100%;
			box-sizing: border-box;
		}

		// for Opera, Chrome, Edge, and Safari, Firefox
		:host::-webkit-scrollbar {
			display: none;
		}

		@media (max-width: 768px) {
			:host {
				align-self: flex-start;
				padding-top: var(--large-padding);
				padding-left: var(--small-padding);
				padding-right: var(--small-padding);
			}

			.container {
				width: 100%;
				padding-left: 0;
				padding-right: 0;
			}
		}
		.features {
			break-inside: avoid;
			padding-bottom: var(--large-space);
			width: 100%;
			pointer-events: none;
			display: none;
			flex-flow: column;
			place-content: flex-start;
			gap: var(--large-space);
			margin-bottom: 90px;
		}

		.features:has(> *) {
			pointer-events: all;
		}
		.header {
			font: var(--font-widget-label);
			color: var(--color-grey4);
			padding-bottom: var(--small-padding);
			border-bottom: 1px solid var(--color-white);
			display: none;
		}
		.dashboard .header {
			display: block;
		}
		.features > * {
			pointer-events: auto;
		}

		@media (max-width: 768px) {
			:host {
				padding: 0 var(--small-padding) var(--small-padding);
			}

			.features {
				min-width: calc(100vw - var(--small-padding) * 2);
				padding-bottom: 90px;
			}
		}

		.active {
			display: flex;
			pointer-events: all
		}
		.dashboard {
			display: flex;
			pointer-events: all
		}
    `;

	@property({type: Array})
	featureList = [];

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.translations = state.productData.translations;
		this.productData = state.productData.productData;
		this.features = state.featureModel.data.features ?? [];
		this.sectionConfig = selectSectionConfig(state);
		this.decisions = state.decisions.data;
		this.region = state.status.region;
		this.lang = state.status.lang;
		this.currentSection = selectCurrentSection(state);
		this.dashboardActive = state.status.dashboardActive;
		this.isLightProfile = state.status.family === 'LightProfile';
		this.isSolar = state.status.family === 'Aeto';
	}
	isSatisfied(feature) {
		return (feature.variableId in this.decisions.normalizedFeatures && this.decisions.normalizedFeatures[feature.variableId].isSatisfied);
	}
	canBeOmitted(feature) {
		// satisfied and only one value available
		return this.isSatisfied(feature) && feature.values.filter(value => {
			const key = feature.variableId + '.' + value.featureValueId;
			return (key in this.decisions.normalizedDecisions && this.decisions.normalizedDecisions[key].canBeIncluded);
		}).length <= 1;
	}
	render() {
		//TODO: remove hack to get the model select into the luminaire section
		// const filtered = this.features
		// 	.filter(feature => !(this.isSolar && !selectSolarLumPosition2IsVisible(this.state) && feature.variableId === 'LE_LightDistribution_2'))
		// 	.filter(feature => !this.isLightProfile || luminaryOptions.find(luminary => luminary.id === selectLuminary(this.state))?.features.includes(feature.variableId))
		// 	.filter(feature => {
		// 		const featureProductData = getProductData(feature.variableId, this.productData);
		// 		console.log('featureProductData', feature.variableId, this.productData, featureProductData);
		//
		// 		return featureProductData?.isVisible && regionCheck(featureProductData, this.region);
		// 	})
		// 	.sort((a, b) => getProductData(a.variableId, this.productData).displaySequenceUi - getProductData(b.variableId, this.productData).displaySequenceUi);
		// console.log('filtered', filtered);

		return html`
			<div class="container ${classMap({columns: this.dashboardActive})}">
				${repeat(
					this.sectionConfig,
					(section) => html`
						${when(this.dashboardActive || (!this.dashboardActive && section.id === this.currentSection), () => html`
							<div class="features ${classMap({
								active: !this.dashboardActive && section.id === this.currentSection,
								dashboard: this.dashboardActive
							})}">
								<div class="header">${section.label[this.lang]}</div>
								${(this.dashboardActive || section.id === this.currentSection) ? html`
									${section.widgets}
									${this.isLightProfile && section.id === 'luminaire' ? luminaryOptions.find(luminary => luminary.id === selectLuminary(this.state))?.widgets : null}
									${repeat(
										this.features
											.filter(feature => !(this.isSolar && !selectSolarLumPosition2IsVisible(this.state) && feature.variableId === 'LE_LightDistribution_2'))
											//.filter(feature => !this.isLightProfile || luminaryOptions.find(luminary => luminary.id === selectLuminary(this.state))?.features.includes(feature.variableId))
											.filter(feature => featureInSection(this.sectionConfig, section, feature.variableId))
											.filter(feature => {
												const featureProductData = getProductData(feature.variableId, this.productData);
												return featureProductData?.isVisible && regionCheck(featureProductData, this.region);
											})
											.filter(feature => this.isSolar || !this.canBeOmitted(feature))
											.sort((a, b) => getProductData(a.variableId, this.productData).displaySequenceUi - getProductData(b.variableId, this.productData).displaySequenceUi),
										(feature) => feature.variableId, (feature, index) => {
											switch (getProductData(feature.variableId, this.productData).displayStyle) {
												case 'colorSelect' :
													return html`
														<feature-color-select .feature=${feature}
																			  .productData=${getProductData(feature.variableId, this.productData)}></feature-color-select>
													`;

												case 'compactSelect' :
													return html`
														<feature-compact-select .feature=${feature}
																				.productData=${getProductData(feature.variableId, this.productData)}></feature-compact-select>
													`;
												case 'comboSelect' :
													return html`
														<feature-combo-select .feature=${feature}
																			  .productData=${getProductData(feature.variableId, this.productData)}></feature-combo-select>
													`;
												case 'reducedSelect' :
													return html`
														<feature-compact-select .feature=${feature} .hideDisabled=${true}
																				.productData=${getProductData(feature.variableId, this.productData)}></feature-compact-select>
													`;
												case 'choiceSelect' :
													return html`
														<feature-choice-select .feature=${feature}
																			   .productData=${getProductData(feature.variableId, this.productData)}></feature-choice-select>
													`;
												case 'reducedChoiceSelect' :
													return html`
														<feature-choice-select .hideDisabled=${true}
																			   .feature=${feature}
																			   .productData=${getProductData(feature.variableId, this.productData)}></feature-choice-select>
													`;
												default :
													return html`
														<feature-compact-select .feature=${feature} .hideDisabled=${this.isLightProfile}
																				.productData=${getProductData(feature.variableId, this.productData)}></feature-compact-select>
													`;
											}
										})
									}` : null}
							</div>
						`)}
					`)
				}
			</div>
        `;
	}
}
